/**
 * The realtime MQTT-over-WSS connection (TO-468, ADR-0049). A plain module, not
 * React, so the connection outlives renders. Subscribe-only.
 *
 * - Every attempt reads a FRESH access token (a reconnect an hour later must not
 *   reuse a dead token); the client library's own reconnect is disabled.
 * - A SUBACK reason code ≥ 128 is a FAILURE — otherwise the client would sit
 *   "connected, subscribed to nothing" forever.
 * - Retries back off with jitter (`backoffDelayMs`), reset by a successful subscribe.
 * - Payloads are validated structurally before dispatch; anything else is dropped.
 */
import { buildUserTopic, isRealtimeMessage, REALTIME_SUBSCRIBE_LEAF, type RealtimeMessage } from '@today/types'
import { backoffDelayMs } from './realtime-backoff'
import type { RealtimeConfig } from './realtime-config'
import { dispatchRealtimeMessage, RealtimeConnectionState, setRealtimeConnectionState } from './realtime-dispatcher'

export interface MqttConnectOptions {
	protocolVersion: 5
	username: string
	password: string
	reconnectPeriod: 0
	manualConnect: true
}

type SubackCallback = (err: Error | null, granted?: Array<{ qos: number }>) => void

/** The subset of the `mqtt` client the connection uses. */
export interface MqttClientLike {
	on(event: 'connect' | 'close', cb: () => void): unknown
	on(event: 'error', cb: (error: Error) => void): unknown
	on(event: 'message', cb: (topic: string, payload: Uint8Array) => void): unknown
	subscribe(topic: string, opts: { qos: 1 }, cb: SubackCallback): unknown
	connect(): unknown
	end(force?: boolean): unknown
}

interface RealtimeLog {
	error: (...args: unknown[]) => void
	warn: (...args: unknown[]) => void
}

export interface RealtimeConnectionDeps {
	config: RealtimeConfig
	tenantId: string
	userId: string
	/** Resolved per attempt — never captured once. `null` fails the attempt. */
	getToken: () => Promise<string | null>
	connect?: (url: string, options: MqttConnectOptions) => Promise<MqttClientLike>
	onMessage?: (message: RealtimeMessage) => void
	setState?: (state: RealtimeConnectionState) => void
	random?: () => number
	setTimer?: (fn: () => void, ms: number) => unknown
	clearTimer?: (handle: unknown) => void
	log?: RealtimeLog
	isDev?: boolean
}

/** Loads `mqtt` only when a connection is actually started (code-split; never for a null config). */
const defaultConnect = async (url: string, options: MqttConnectOptions): Promise<MqttClientLike> => {
	const { connect } = await import('mqtt')
	// `MqttClient` is wider than this interface; a single assertion narrows it
	// (its EventEmitter `on` overloads are not inferable as an implementation).
	return connect(url, options) as MqttClientLike
}

/** Parse + validate one payload; `undefined` when it is not a well-formed message. */
function parseMessage(payload: Uint8Array): RealtimeMessage | undefined {
	try {
		const parsed: unknown = JSON.parse(new TextDecoder().decode(payload))
		return isRealtimeMessage(parsed) ? parsed : undefined
	} catch {
		return undefined
	}
}

class RealtimeConnection {
	private readonly url: string
	private readonly filter: string
	private readonly connect: NonNullable<RealtimeConnectionDeps['connect']>
	private readonly onMessage: (message: RealtimeMessage) => void
	private readonly setState: (state: RealtimeConnectionState) => void
	private readonly random: () => number
	private readonly setTimer: (fn: () => void, ms: number) => unknown
	private readonly clearTimer: (handle: unknown) => void
	private readonly log: RealtimeLog
	private readonly isDev: boolean
	private stopped = false
	private failures = 0
	private attemptId = 0
	private failedAttempt = -1
	private client: MqttClientLike | undefined
	private timer: unknown

	constructor(private readonly deps: RealtimeConnectionDeps) {
		const { config } = deps
		this.url = `wss://${config.endpoint}/mqtt?x-amz-customauthorizer-name=${encodeURIComponent(config.authorizer)}`
		this.filter = buildUserTopic({
			app: config.app,
			stage: config.stage,
			tenantId: deps.tenantId,
			userId: deps.userId,
			entityType: REALTIME_SUBSCRIBE_LEAF,
		})
		this.connect = deps.connect ?? defaultConnect
		this.onMessage = deps.onMessage ?? dispatchRealtimeMessage
		this.setState = deps.setState ?? setRealtimeConnectionState
		this.random = deps.random ?? Math.random
		this.setTimer = deps.setTimer ?? ((fn, ms) => setTimeout(fn, ms))
		this.clearTimer = deps.clearTimer ?? ((handle) => clearTimeout(handle as ReturnType<typeof setTimeout>))
		this.log = deps.log ?? console
		this.isDev = deps.isDev ?? import.meta.env.DEV
	}

	/** True when attempt `id` was superseded, already failed, or the connection stopped. */
	private stale(id: number): boolean {
		return this.stopped || id !== this.attemptId || this.failedAttempt === id
	}

	/** End attempt `id` and schedule the next one after the jittered backoff. */
	private fail(id: number): void {
		if (this.stale(id)) return
		this.failedAttempt = id
		this.client?.end(true)
		this.client = undefined
		this.setState(RealtimeConnectionState.RECONNECTING)
		this.timer = this.setTimer(() => void this.attempt(), backoffDelayMs(this.failures, this.random))
		this.failures++
	}

	private handleIncoming(payload: Uint8Array): void {
		const message = parseMessage(payload)
		if (message) {
			this.onMessage(message)
			return
		}
		const note = 'realtime: dropped a malformed message'
		if (this.isDev) this.log.error(note)
		else this.log.warn(note)
	}

	private onSuback(id: number, err: Error | null, granted?: Array<{ qos: number }>): void {
		if (err || granted?.some((g) => g.qos >= 128)) {
			this.fail(id)
			return
		}
		if (this.stale(id)) return
		this.failures = 0
		this.setState(RealtimeConnectionState.CONNECTED)
	}

	private wire(id: number, client: MqttClientLike): void {
		client.on('connect', () => {
			client.subscribe(this.filter, { qos: 1 }, (err, granted) => this.onSuback(id, err, granted))
		})
		client.on('message', (_topic, payload) => this.handleIncoming(payload))
		client.on('error', () => this.fail(id))
		client.on('close', () => this.fail(id))
		client.connect()
	}

	async attempt(): Promise<void> {
		const id = ++this.attemptId
		this.setState(RealtimeConnectionState.CONNECTING)
		const token = await this.deps.getToken()
		if (this.stale(id)) return
		if (!token) {
			this.fail(id)
			return
		}

		const options: MqttConnectOptions = {
			protocolVersion: 5,
			username: '',
			password: token,
			reconnectPeriod: 0,
			manualConnect: true,
		}
		let client: MqttClientLike
		try {
			client = await this.connect(this.url, options)
		} catch {
			this.fail(id)
			return
		}
		if (this.stale(id)) {
			client.end(true)
			return
		}
		this.client = client
		this.wire(id, client)
	}

	stop(): void {
		this.stopped = true
		if (this.timer !== undefined) this.clearTimer(this.timer)
		this.client?.end(true)
		this.client = undefined
		this.setState(RealtimeConnectionState.IDLE)
	}
}

/** Start the connection; returns `stop()`. */
export function startRealtimeConnection(deps: RealtimeConnectionDeps): () => void {
	const connection = new RealtimeConnection(deps)
	void connection.attempt()
	return () => connection.stop()
}
